import { useState } from "react";
import { useChannelSettings } from "../../hooks/useChannelSettings";
import type { ChannelType } from "../../types/subscriptionSettings";
import { CHANNEL_TYPES } from "../../types/subscriptionSettings";
import { EmailChannelTab } from "../../components/organisms/subscription/EmailChannelTab";
import { SmsChannelTab } from "../../components/organisms/subscription/SmsChannelTab";
import { TeamsChannelTab } from "../../components/organisms/subscription/TeamsChannelTab";
import { WebhookChannelTab } from "../../components/organisms/subscription/WebhookChannelTab";
import "../../styles/subscription-settings.css";

const TAB_LABELS: Record<ChannelType, string> = {
  email: "Email",
  sms: "SMS",
  teams: "Teams",
  webhook: "Webhook",
};

export function SubscriptionSettingsPage() {
  const [active, setActive] = useState<ChannelType>("email");
  const ctrl = useChannelSettings();

  if (ctrl.error) {
    return (
      <div className="ac-page">
        <h1 className="ac-page__title">Subscription Settings</h1>
        <div className="ac-alert ac-alert--error">{ctrl.error}</div>
      </div>
    );
  }

  return (
    <div className="ac-page ac-subscription-settings">
      <header className="ac-page__header">
        <h1 className="ac-page__title">Subscription Settings</h1>
        <p className="ac-page__subtitle">
          Configure notification channels (Email, SMS, Teams, Webhook), validate credentials with a
          test send, and enable or disable each channel independently.
        </p>
      </header>

      <div className="ac-tab-strip" role="tablist" aria-label="Notification channels">
        {CHANNEL_TYPES.map((ct) => (
          <button
            key={ct}
            role="tab"
            type="button"
            aria-selected={active === ct}
            className={`ac-tab-strip__tab ${active === ct ? "ac-tab-strip__tab--active" : ""}`}
            onClick={() => setActive(ct)}
          >
            {TAB_LABELS[ct]}
          </button>
        ))}
      </div>

      {ctrl.isLoading || !ctrl.settings ? (
        <div className="ac-loading">Loading channel settings...</div>
      ) : (
        <div className="ac-tab-content">
          {/*
            All five tabs stay mounted to preserve unsaved input across tab switches (FR-05).
            Inactive tabs are hidden via CSS, not unmounted.
          */}
          <div hidden={active !== "email"}>
            <EmailChannelTab ctrl={ctrl} setting={ctrl.settings.email} />
          </div>
          <div hidden={active !== "sms"}>
            <SmsChannelTab ctrl={ctrl} setting={ctrl.settings.sms} />
          </div>
          <div hidden={active !== "teams"}>
            <TeamsChannelTab ctrl={ctrl} setting={ctrl.settings.teams} />
          </div>
          <div hidden={active !== "webhook"}>
            <WebhookChannelTab ctrl={ctrl} setting={ctrl.settings.webhook} />
          </div>
        </div>
      )}
    </div>
  );
}
